import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import React, { type ReactElement, useEffect, useRef } from 'react'
import {
  type DefaultValues,
  type FieldValues,
  FormProvider,
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
  useWatch
} from 'react-hook-form'
import { useDebouncedCallback } from 'use-debounce'
import type z from 'zod'
import { type ApiResponseError, getApiViolations } from '~/api/utils'
import { formClass, formClassLarge } from './Form.css'
import { GlobalAlert } from './GlobalAlert'
import { Submit } from './Submit'

interface FormProps<T extends FieldValues>
  extends Omit<
    React.HTMLAttributes<HTMLFormElement>,
    'autoSave' | 'onSubmit' | 'onError'
  > {
  className?: string
  children: ReactElement | (ReactElement | null | undefined | false)[]
  defaultValues?: DefaultValues<T>
  onSubmit: SubmitHandler<T>
  onError?: SubmitErrorHandler<T>
  globalError?: null | string
  globalErrorInSubmit?: boolean
  schema?: z.Schema<T>
  onDirtyStateChange?: (isDirty: boolean) => void
  largeFormStyles?: boolean
  autoSave?: boolean
  autoSaveDelay?: number
}

export const Form = <T extends FieldValues>({
  className,
  defaultValues,
  children,
  onSubmit,
  onError,
  onDirtyStateChange,
  globalError,
  globalErrorInSubmit = false,
  schema,
  autoSave = false,
  autoSaveDelay = 1000,
  largeFormStyles = false,
  ...rest
}: FormProps<T>): ReactElement => {
  if (children === undefined || children === null) {
    throw new Error('Form must have children')
  }
  const methods = useForm({
    defaultValues,
    resolver: schema !== undefined ? zodResolver(schema as any) : undefined,
    mode: 'onChange'
  })
  const { handleSubmit, formState, reset, getValues, control, setError } =
    methods
  const dirtyFieldsKeys = Object.keys(formState.dirtyFields).join(',')
  const isSavingRef = useRef(false)
  const watchedValues = useWatch({ control })

  useEffect(() => {
    onDirtyStateChange?.(dirtyFieldsKeys.length > 0)
  }, [dirtyFieldsKeys, onDirtyStateChange])

  const submitWithServerErrors = async (data: T): Promise<boolean> => {
    try {
      await onSubmit(data)
      return true
    } catch (error) {
      const violations = getApiViolations(error as ApiResponseError)
      if (violations.length === 0) {
        throw error
      }
      violations.forEach(({ propertyPath }) => {
        setError(propertyPath as any, {
          type: 'server',
          message: 'Ce champ est obligatoire.'
        })
      })
      return false
    }
  }

  const debouncedAutoSave = useDebouncedCallback(async () => {
    if (isSavingRef.current) return
    if (Object.keys(formState.dirtyFields).length === 0) return
    isSavingRef.current = true
    try {
      await handleSubmit(async (data) => {
        const succeeded = await submitWithServerErrors(data)
        if (succeeded) {
          reset(getValues(), { keepValues: true })
        }
      }, onError)()
    } finally {
      isSavingRef.current = false
    }
  }, autoSaveDelay)

  useEffect(() => {
    if (autoSave && watchedValues) {
      debouncedAutoSave()
    }
  }, [autoSave, watchedValues, debouncedAutoSave])

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(submitWithServerErrors, onError)}
        className={clsx(
          formClass,
          largeFormStyles && formClassLarge,
          className
        )}
        {...rest}
      >
        {recursiveMap(
          Array.isArray(children)
            ? children.filter(
                (c) => c !== null && c !== undefined && c !== false
              )
            : children,
          (child: ReactElement) => {
            if (
              child?.type === Submit ||
              (child?.type as any)?.displayName === 'Submit'
            ) {
              return React.createElement(child.type, {
                ...{
                  ...child.props,
                  isSubmitting: formState.isSubmitting,
                  isSubmitSuccessful: formState.isSubmitSuccessful,
                  isSubmitted: formState.isSubmitted,
                  globalError,
                  globalErrorInSubmit,
                  key: 'submit'
                }
              })
            }
            if (
              child?.type === GlobalAlert ||
              (child?.type as any)?.displayName === 'GlobalAlert'
            ) {
              return React.createElement(child.type, {
                ...{
                  ...child.props,
                  error:
                    globalError !== '' &&
                    (!globalErrorInSubmit || !formState.isSubmitSuccessful)
                      ? globalError
                      : ''
                }
              })
            }
            if (child?.props?.name !== undefined) {
              return React.createElement(child.type, {
                ...{
                  ...child.props,
                  error: formState.errors[child.props.name],
                  register: methods.register,
                  control: methods.control,
                  setValue: methods.setValue,
                  watch: methods.watch,
                  key: child.props.name
                }
              })
            }
            return child
          }
        )}
      </form>
    </FormProvider>
  )
}
const recursiveMap = (
  children: ReactElement[] | ReactElement,
  fn: (child: ReactElement) => ReactElement
): ReactElement[] => {
  return React.Children.map(children, (child) => {
    let newChild = child
    if (!React.isValidElement(newChild)) {
      return newChild
    }

    if ((newChild as ReactElement).props.children) {
      const props = {
        children: recursiveMap((newChild as ReactElement).props.children, fn)
      }
      newChild = React.cloneElement(newChild, props)
    }

    return fn(newChild)
  })
}
